/**
 * EltJobsPage Component
 *
 * Main page for managing ELT jobs with export, import, and copy support.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { ArrowPathIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { EltJobTable } from '../components/EltJobTable';
import { CreateEltJobModal } from '../components/CreateEltJobModal';
import { UpdateEltJobModal } from '../components/UpdateEltJobModal';
import { DeleteEltJobModal } from '../components/DeleteEltJobModal';
import { EltJobDetailsModal } from '../components/EltJobDetailsModal';
import { TriggerJobModal } from '../components/TriggerJobModal';
import { ImportEltJobsModal } from '../components/ImportEltJobsModal';
import { CopyEltJobModal } from '../components/CopyEltJobModal';
import { useEltJobs } from '../hooks/useEltJobs';
import { useExportEltJobs } from '../hooks/useExportEltJobs';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { EltJob } from '../../../types/elt-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Trigger a browser file download with the given JSON data
 */
function downloadJsonFile(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const EltJobsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<EltJob | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showTriggerModal, setShowTriggerModal] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [exportError, setExportError] = useState<string>('');

  const tenantId = tenant?.tenantId || '';

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch ELT jobs
  const {
    data: jobsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEltJobs(tenantId, {
    search: debouncedSearch || undefined,
  });

  const eltJobs = jobsData || [];

  // Export mutation
  const { mutate: exportJobs, isPending: isExporting } = useExportEltJobs(tenantId, {
    onSuccess: (data) => {
      const date = new Date().toISOString().slice(0, 10);
      downloadJsonFile(data, `elt-jobs-export-${date}.json`);
      setExportError('');
      setSelectedJobIds(new Set());
    },
    onError: (error) => {
      setExportError(error);
    },
  });

  // Filter locally
  const filteredJobs = useMemo(() => {
    if (!eltJobs) return [];

    let filtered = [...eltJobs];

    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.eltJobName.toLowerCase().includes(query) ||
          job.eltJobCode.toLowerCase().includes(query) ||
          (job.description && job.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [eltJobs, debouncedSearch]);

  const handleViewDetails = (job: EltJob) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const handleEdit = (job: EltJob) => {
    setSelectedJob(job);
    setShowUpdateModal(true);
  };

  const handleDelete = (job: EltJob) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const handleTrigger = (job: EltJob) => {
    setSelectedJob(job);
    setShowTriggerModal(true);
    setShowDetailsModal(false);
  };

  const handleCopy = (job: EltJob) => {
    setSelectedJob(job);
    setShowCopyModal(true);
  };

  const handleExportSelected = useCallback(() => {
    setExportError('');
    const ids = Array.from(selectedJobIds);
    exportJobs(ids.length > 0 ? ids : undefined);
  }, [selectedJobIds, exportJobs]);

  const handleExportAll = useCallback(() => {
    setExportError('');
    exportJobs(undefined);
  }, [exportJobs]);

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>ELT Jobs</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Configure ELT jobs with scheduling and output options
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowImportModal(true)}
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
              Import
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              Create ELT Job
            </Button>
          </div>
        </div>

        {/* Search + Export controls */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search ELT jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {selectedJobIds.size > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportSelected}
              disabled={isExporting}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Export Selected ({selectedJobIds.size})
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportAll}
            disabled={isExporting || eltJobs.length === 0}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            Export All
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alerts */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}
        {exportError && (
          <Alert variant="error">
            Export failed: {exportError}
          </Alert>
        )}

        {/* ELT Jobs Table */}
        <EltJobTable
          eltJobs={filteredJobs}
          isLoading={isLoading}
          selectedJobIds={selectedJobIds}
          onSelectionChange={setSelectedJobIds}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onTrigger={handleTrigger}
          onCopy={handleCopy}
        />

        {/* Modals */}
        <CreateEltJobModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedJob && (
          <EltJobDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedJob(null);
            }}
            tenantId={tenantId}
            eltJobId={selectedJob.eltJobId}
            onTrigger={handleTrigger}
          />
        )}

        {showUpdateModal && selectedJob && (
          <UpdateEltJobModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedJob(null);
            }}
            tenantId={tenantId}
            eltJob={selectedJob}
          />
        )}

        {showDeleteModal && selectedJob && (
          <DeleteEltJobModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedJob(null);
            }}
            eltJob={selectedJob}
          />
        )}

        {showTriggerModal && selectedJob && (
          <TriggerJobModal
            isOpen={showTriggerModal}
            onClose={() => {
              setShowTriggerModal(false);
              setSelectedJob(null);
            }}
            tenantId={tenantId}
            eltJob={selectedJob}
          />
        )}

        <ImportEltJobsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          tenantId={tenantId}
        />

        {showCopyModal && selectedJob && (
          <CopyEltJobModal
            isOpen={showCopyModal}
            onClose={() => {
              setShowCopyModal(false);
              setSelectedJob(null);
            }}
            tenantId={tenantId}
            sourceJob={selectedJob}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
